import { memo, type ReactNode, useCallback } from 'react';

import { EyeClosedIcon, EyeIcon } from '@air/design-icons';
import { dropdownMenuItem } from '@air/primitive-dropdown-menu';

import { VISIBILITY_TOGGLE } from '~/constants/testIDs';
import { type ConfigViewOption } from '~/store/configViews/types';

export type ViewVisibilityToggleProps = {
  option: ConfigViewOption;
  /** Receives the row's option so parents can pass one stable handler to every row instead of binding per row. */
  onToggleOption: (option: ConfigViewOption) => void;
  prefix?: ReactNode;
};

export const ViewVisibilityToggle = memo(({ option, onToggleOption, prefix }: ViewVisibilityToggleProps) => {
  const handleClick = useCallback(() => {
    onToggleOption(option);
  }, [onToggleOption, option]);

  return (
    <button
      className={dropdownMenuItem({ size: 'small' })}
      data-testid={VISIBILITY_TOGGLE}
      data-field-name={option.fieldName}
      onClick={handleClick}
      type="button"
    >
      {prefix ? <div className="mr-2 shrink-0">{prefix}</div> : null}

      <div className="flex min-w-0 grow truncate text-14">{option.label}</div>

      <div className="ml-2 shrink-0 text-gray-10">
        {option.isVisible ? (
          // biome-ignore lint/plugin/no-compound-data-testid: pre-existing — Cypress specs query this interpolated value directly; renaming requires updating those specs
          <EyeIcon data-testid={`${option.fieldName}-visible`} size="sm" />
        ) : (
          // biome-ignore lint/plugin/no-compound-data-testid: pre-existing — Cypress specs query this interpolated value directly; renaming requires updating those specs
          <EyeClosedIcon data-testid={`${option.fieldName}-not-visible`} size="sm" />
        )}
      </div>
    </button>
  );
});

ViewVisibilityToggle.displayName = 'ViewVisibilityToggle';
